/**
 * React components for rendering formatted audit log data.
 */

import { Text, Box, Stack, Grid } from '@customerio/pluma-components/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import React from 'react';

import { isJSONString, formatValue } from './value-formatting';
import { formatAttributeName } from './name-formatting';
import { isISOTimestamp, formatHumanFriendlyDateFromISO } from './date-formatting';

// Props interface for FriendlyJSONDisplay component
interface FriendlyJSONDisplayProps {
  value: unknown;
  textColor?: string;
  parentKey?: string;
}

/**
 * Helper component to render JSON in a user-friendly table/list format.
 * Memoized to prevent unnecessary re-renders in recursive structures.
 */
export const FriendlyJSONDisplay = React.memo<FriendlyJSONDisplayProps>(function FriendlyJSONDisplay({
  value,
  textColor,
  parentKey,
}) {
  // Handle null/undefined
  if (value == null) {
    return (
      <Text
        size="s"
        style={{
          fontFamily: 'monospace',
          color: textColor,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        —
      </Text>
    );
  }

  // If it's a string, check if it's JSON and parse it
  if (typeof value === 'string') {
    if (isJSONString(value)) {
      try {
        const parsed = JSON.parse(value);
        return <FriendlyJSONDisplay value={parsed} textColor={textColor} parentKey={parentKey} />;
      } catch {
        // If parsing fails, fall through to display as string
      }
    }
    // Check if it's an ISO date string
    if (isISOTimestamp(value)) {
      return (
        <Text
          size="s"
          style={{
            fontFamily: 'monospace',
            color: textColor,
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
          }}
        >
          {formatHumanFriendlyDateFromISO(value)}
        </Text>
      );
    }
    // Display as regular string
    return (
      <Text
        size="s"
        style={{
          fontFamily: 'monospace',
          color: textColor,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {value}
      </Text>
    );
  }

  if (Array.isArray(value)) {
    return (
      <Stack gap="100">
        {value.map((item, index) => (
          <Box
            key={index}
            p="150"
            style={{
              borderRadius: themeVars.radius['4'],
              border: `${themeVars.border['width-0125']} solid ${themeVars.color['border-base']}`,
              backgroundColor: themeVars.color['surface-base'],
            }}
          >
            <Text size="xs" weight="medium" color="subtle" style={{ marginBottom: '8px' }}>
              Item {index + 1}:
            </Text>
            <FriendlyJSONDisplay value={item} textColor={textColor} parentKey={parentKey} />
          </Box>
        ))}
      </Stack>
    );
  }

  if (typeof value === 'object') {
    const object = value as Record<string, unknown>;
    const entries = Object.entries(object);

    // Check if all values are simple primitives (string, number, boolean, null)
    const allSimple = entries.every(([, value_]) => {
      const type = typeof value_;
      return type === 'string' || type === 'number' || type === 'boolean' || value_ == null;
    });

    // Check if this is user_flags or account_flags - render on separate full-width rows
    const parentKeyLower = parentKey?.toLowerCase() ?? '';
    const isFlagsObject =
      parentKeyLower === 'user_flags' ||
      parentKeyLower === 'userflags' ||
      parentKeyLower === 'account_flags' ||
      parentKeyLower === 'accountflags';

    if (isFlagsObject) {
      // Render flags in two columns to save vertical space
      return (
        <Grid columns={2} gap="100">
          {entries.map(([key, value_]) => (
            <Box
              key={key}
              display="flex"
              gap="150"
              px="300"
              py="200"
              style={{
                width: '100%',
                minWidth: 0,
              }}
            >
              <Text
                size="s"
                weight="medium"
                style={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  flexShrink: 0,
                  whiteSpace: 'normal',
                  minWidth: '150px',
                }}
              >
                {formatAttributeName(key)}:
              </Text>
              <Text
                size="s"
                style={{
                  fontFamily: 'monospace',
                  color: textColor,
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'normal',
                }}
              >
                {formatValue(value_)}
              </Text>
            </Box>
          ))}
        </Grid>
      );
    }

    // Use grid layout for objects with many simple key-value pairs
    if (allSimple && entries.length > 3) {
      return (
        <Grid columns="repeat(auto-fill, minmax(250px, 1fr))" gap="100">
          {entries.map(([key, value_]) => (
            <Box
              key={key}
              display="flex"
              gap="100"
              px="200"
              py="100"
              style={{
                minWidth: 0,
                width: '100%',
              }}
            >
              <Text
                size="s"
                weight="medium"
                style={{
                  fontFamily: 'monospace',
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  flexShrink: 0,
                  whiteSpace: 'normal',
                }}
              >
                {formatAttributeName(key)}:
              </Text>
              <Text
                size="s"
                style={{
                  fontFamily: 'monospace',
                  color: textColor,
                  wordBreak: 'break-word',
                  overflowWrap: 'break-word',
                  flex: 1,
                  minWidth: 0,
                  whiteSpace: 'normal',
                }}
              >
                {formatValue(value_)}
              </Text>
            </Box>
          ))}
        </Grid>
      );
    }

    // Use vertical list for complex objects or small objects
    return (
      <Stack gap="050">
        {entries.map(([key, value_]) => (
          <Box
            key={key}
            display="flex"
            gap="150"
            py="100"
            style={{
              borderBottom: `${themeVars.border['width-0125']} solid ${themeVars.color['border-base']}`,
              minWidth: 0, // Allow flex item to shrink below its content size
            }}
          >
            <Text
              size="s"
              weight="medium"
              style={{
                minWidth: '150px',
                fontFamily: 'monospace',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                flexShrink: 0,
              }}
            >
              {formatAttributeName(key)}:
            </Text>
            <Box style={{ flex: 1, minWidth: 0, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
              <FriendlyJSONDisplay value={value_} textColor={textColor} parentKey={key} />
            </Box>
          </Box>
        ))}
      </Stack>
    );
  }

  // For other primitive values (numbers, booleans)
  if (typeof value === 'boolean') {
    return (
      <Text
        size="s"
        style={{
          fontFamily: 'monospace',
          color: textColor,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
        }}
      >
        {value ? 'yes' : 'no'}
      </Text>
    );
  }
  return (
    <Text
      size="s"
      style={{
        fontFamily: 'monospace',
        color: textColor,
        wordBreak: 'break-word',
        overflowWrap: 'break-word',
      }}
    >
      {String(value)}
    </Text>
  );
});
